/**
 * Builds the consolidated roster for one session, from the dated theme list and
 * "Quantum Healing Subscribers".
 *
 * The fulfillment now copies members onto every upcoming theme list, so the theme
 * list alone is usually the full roster. The subscribers list is still read for
 * two reasons: it is the only way to tell a member from a one-time booking, and
 * it still surfaces anyone a failed sync left off the theme list.
 */

/** One row as returned by GET /api3.2/mail-list/:listId/export. */
export interface RosterContact {
	firstName: string;
	lastName: string;
	workEmail: string;
	personalEmail: string;
	primaryPhone: string;
	secondaryPhone: string;
	notes: string;
	company: string;
}

export type ParticipantType = 'member' | 'one-time';

export interface Participant {
	/** Stable row id for the grid; also the dedup key. */
	id: string;
	name: string;
	email: string;
	phone: string;
	/**
	 * When they paid for this session, or "Member since <date>" for members;
	 * '' when neither is known.
	 */
	purchased: string;
	/** ISO timestamp behind `purchased`, so the column sorts by date; '' when unknown. */
	purchasedAt: string;
	type: ParticipantType;
}

/**
 * The export endpoint returns no contact_id, but email is safe as the key: the
 * Stripe fulfillment resolves every contact through findContactByEmail, which
 * matches on LOWER(email_work), so two rows sharing an email are the same
 * contact. Falls back to name so a contact with no work email still appears
 * instead of colliding with every other blank. The last resort is scoped to its
 * list, or row 0 of the theme list and row 0 of the subscribers list — two
 * different people — would merge into one.
 */
function identityKey(contact: RosterContact, index: number, type: ParticipantType): string {
	const email = contact.workEmail?.trim().toLowerCase();
	if (email) return email;

	const name = `${contact.firstName} ${contact.lastName}`.trim().toLowerCase();
	return name ? `name:${name}` : `row:${type}:${index}`;
}

function displayName(contact: RosterContact): string {
	const name = `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim();
	return name || '(no name)';
}

/**
 * The checkout stores whatever the buyer typed, so these arrive as bare digits.
 * A 10-digit North American number gets the familiar grouping, with a leading
 * country code kept in front of it. Anything else — an extension, an
 * international number, a half-filled field — is left exactly as entered rather
 * than grouped into something wrong.
 */
export function formatPhone(raw: string): string {
	const digits = raw.replace(/\D/g, '');

	if (digits.length === 10) {
		return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
	}

	if (digits.length === 11 && digits.startsWith('1')) {
		return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
	}

	return raw;
}

/** Prefer the work number the checkout writes; fall back to the cell. */
function displayPhone(contact: RosterContact): string {
	const raw = contact.primaryPhone?.trim() || contact.secondaryPhone?.trim() || '';
	return raw ? formatPhone(raw) : '';
}

function toParticipant(
	contact: RosterContact,
	index: number,
	type: ParticipantType
): Participant {
	return {
		id: identityKey(contact, index, type),
		name: displayName(contact),
		email: contact.workEmail?.trim() || '',
		phone: displayPhone(contact),
		purchased: '',
		purchasedAt: '',
		type,
	};
}

/**
 * Kept out of the merge so the roster can show before Stripe answers, and
 * still builds if it never does.
 */
export function withPurchaseDates(
	participants: Participant[],
	purchaseFor: (participant: Participant) => { label: string; date: string }
): Participant[] {
	return participants.map((p) => {
		const { label, date } = purchaseFor(p);
		return { ...p, purchased: label, purchasedAt: date };
	});
}

/**
 * Merge both lists into one roster, sorted by name. Members are seeded first so
 * anyone on both lists keeps the member badge — the fulfillment copies members
 * onto every upcoming theme list, so most of them appear in both.
 */
export function buildParticipants(
	themeContacts: RosterContact[],
	subscriberContacts: RosterContact[]
): Participant[] {
	const byKey = new Map<string, Participant>();

	(subscriberContacts ?? []).forEach((contact, i) => {
		const participant = toParticipant(contact, i, 'member');
		byKey.set(participant.id, participant);
	});

	(themeContacts ?? []).forEach((contact, i) => {
		const participant = toParticipant(contact, i, 'one-time');
		const existing = byKey.get(participant.id);

		if (existing) {
			// A phone collected at this checkout may be missing from the older
			// contact row, so fill the gap without overwriting.
			existing.phone = existing.phone || participant.phone;
			return;
		}

		byKey.set(participant.id, participant);
	});

	return Array.from(byKey.values()).sort((a, b) =>
		a.name.localeCompare(b.name)
	);
}

export interface ParticipantCounts {
	total: number;
	members: number;
	oneTime: number;
}

export function countParticipants(participants: Participant[]): ParticipantCounts {
	const members = participants.filter((p) => p.type === 'member').length;
	return {
		total: participants.length,
		members,
		oneTime: participants.length - members,
	};
}

/** Case-insensitive match across name, email and phone. */
export function filterParticipants(
	participants: Participant[],
	search: string,
	type: ParticipantType | 'all'
): Participant[] {
	const term = search.trim().toLowerCase();

	// Digits only, so a number typed plainly still matches the grouped display.
	const digits = term.replace(/\D/g, '');

	return participants.filter((p) => {
		if (type !== 'all' && p.type !== type) return false;
		if (!term) return true;
		return (
			p.name.toLowerCase().includes(term) ||
			p.email.toLowerCase().includes(term) ||
			p.phone.toLowerCase().includes(term) ||
			(digits !== '' && p.phone.replace(/\D/g, '').includes(digits))
		);
	});
}
